import { motion } from "framer-motion";
import { Utensils, BookOpen, Dumbbell, Footprints, Bus, Bike, ArrowRight, Coffee } from "lucide-react";
import { formatMiles, formatTime, useTimeFormat } from "@/lib/time";

const IMG = {
  dining: "https://images.unsplash.com/photo-1631047086640-9d54fc453cba?auto=format&fit=crop&w=800&q=70",
  library: "https://images.pexels.com/photos/159775/library-la-trobe-study-students-159775.jpeg?auto=compress&w=800",
  rec: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=800&q=70",
};
const ICON = { dining: Utensils, library: BookOpen, rec: Dumbbell };
const LABEL = { dining: "Grab lunch", library: "Study session", rec: "Work out" };

function RecCard({ r, onFocus }) {
  const Icon = ICON[r.type] || Coffee;
  return (
    <motion.button
      whileHover={{ y: -4 }}
      onClick={() => onFocus([r.lat, r.lng])}
      data-testid={`rec-${r.id}`}
      className="relative text-left rounded-xl overflow-hidden h-40 w-full border border-border group"
    >
      <img src={r.photo || IMG[r.type]} alt={r.name} loading="lazy" onError={(e) => { if (e.currentTarget.src !== IMG[r.type]) e.currentTarget.src = IMG[r.type]; }} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/10" />
      <div className="absolute top-3 left-3 flex gap-2">
        <span className="bg-white text-black mono text-[11px] font-bold px-2 py-1 rounded">
          {r.to_min} MIN {r.to_min <= 8 ? "WALK" : "AWAY"}
        </span>
        <span className="bg-[#F59E0B] text-black mono text-[11px] font-bold px-2 py-1 rounded">
          FITS GAP
        </span>
      </div>
      <div className="absolute bottom-0 p-4 text-white">
        <div className="flex items-center gap-1.5 mono text-[11px] tracking-widest uppercase text-white/70">
          <Icon size={12} strokeWidth={2} /> {LABEL[r.type]}
        </div>
        <div className="font-display font-semibold text-lg leading-tight mt-0.5">{r.name}</div>
        <div className="text-xs text-white/70 mt-0.5">{r.free_min} min free after travel</div>
      </div>
    </motion.button>
  );
}

export default function GapCard({ gap, index, onFocus }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
      className="bg-white border border-border rounded-xl p-5"
      data-testid={`gap-block-${index}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Gap detected</div>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-2xl font-semibold mono">{gap.gap_min} min</span>
            <span className="text-sm text-muted-foreground mono">{gap.start_label} – {gap.end_label}</span>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{gap.after}</span>
          <ArrowRight size={14} strokeWidth={1.5} />
          <span className="font-semibold text-foreground">{gap.before}</span>
        </div>
      </div>
      {gap.recommendations.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4">Tight window — best to head straight to your next class.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {gap.recommendations.map((r) => (
            <RecCard key={r.id} r={r} onFocus={onFocus} />
          ))}
        </div>
      )}
    </motion.div>
  );
}

export function Leg({ leg, onDirections }) {
  const t = leg.travel;
  const [fmt] = useTimeFormat();
  const busViable = t.bus_min != null;
  const bikeViable = t.bike_min != null;
  const rec = t.recommended;
  const busRec = rec === "bus";
  const bikeRec = rec === "bike";
  const clickable = typeof onDirections === "function";
  const Wrapper = clickable ? "button" : "div";
  const RecIcon = busRec ? Bus : bikeRec ? Bike : Footprints;
  return (
    <Wrapper
      onClick={clickable ? onDirections : undefined}
      data-testid="travel-leg"
      className={`w-full flex items-center gap-3 py-3 text-left rounded-md px-2 -mx-2 transition-colors group ${
        clickable ? "hover:bg-secondary/50 cursor-pointer" : "cursor-default"
      }`}
    >
      <div className={`w-9 h-9 rounded-md flex items-center justify-center text-white shrink-0 ${busRec ? "bg-[#F59E0B]" : bikeRec ? "bg-[#0EA5E9]" : "bg-brand"}`}>
        <RecIcon size={16} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate flex items-center gap-1.5">
          {leg.from} → {leg.to}
          {leg.kind === "event" && (
            <span className="mono text-[11px] font-bold uppercase tracking-widest text-[#92400e] bg-[#F59E0B]/15 rounded px-1.5 py-0.5 shrink-0">Event</span>
          )}
          {clickable && (
            <ArrowRight size={13} strokeWidth={2} className="text-brand opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatMiles(t.distance_m)} · arrive by {formatTime(leg.arrive_by, fmt)}
          {busViable && <> · bus {t.bus_from} → {t.bus_to}</>}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span
          data-testid="leg-walk-min"
          className={`flex items-center gap-1 rounded-md px-2 py-1 mono text-xs font-bold ${
            rec === "walk" ? "bg-brand text-white" : "bg-secondary text-foreground"
          }`}
        >
          <Footprints size={12} strokeWidth={2} /> {t.walk_min} min
        </span>
        {bikeViable && (
          <span
            data-testid="leg-bike-min"
            title="Bike / scooter"
            className={`flex items-center gap-1 rounded-md px-2 py-1 mono text-xs font-bold ${
              bikeRec ? "bg-[#0EA5E9] text-white" : "bg-secondary text-foreground"
            }`}
          >
            <Bike size={12} strokeWidth={2} /> {t.bike_min} min
          </span>
        )}
        {busViable && (
          <span
            data-testid="leg-bus-min"
            className={`flex items-center gap-1 rounded-md px-2 py-1 mono text-xs font-bold ${
              busRec ? "bg-[#F59E0B] text-black" : "bg-secondary text-foreground"
            }`}
          >
            <Bus size={12} strokeWidth={2} /> {t.bus_min} min
          </span>
        )}
      </div>
    </Wrapper>
  );
}
